import toast from 'react-hot-toast';
import { z } from 'zod';
import { create } from 'zustand';

import type { TApplication, TApplicationFilter } from '../components/types';
import type { useMutation, useQuery } from '@tanstack/react-query';
import { APIEndPoints, Request } from '../apis';
// import { HOUR, MINUTE } from '../common/utils/constants';

type ApplicationStoreState = {
  application: { [id: string]: TApplication };
  applications: Array<TApplication> | null;
  setApplications: (applications: Array<TApplication>) => void;
  isLoading: boolean;
  isCancelLoading: boolean;
  setLoading: (loading: boolean) => void;
  setCancelLoading: (loading: boolean) => void;
};

const STARTING_STATE = {
  isLoading: true,
  isCancelLoading: false,
  application: {},
  applications: null
} as const;

export const useApplicationStore = create<ApplicationStoreState>((set) => ({
  ...STARTING_STATE,
  setLoading: (loading: boolean) => {
    set({ isLoading: loading });
  },
  setCancelLoading: (loading: boolean) => {
    set({ isCancelLoading: loading });
  },
  setApplications: (applications = []) => {
    set({
      application: Object.fromEntries(
        applications.map(({ loanApplicationId, ...rest }) => [
          loanApplicationId,
          { loanApplicationId, ...rest }
        ])
      ),
      applications
    });
  },
  clearState: () => {
    set({ ...STARTING_STATE });
  }
}));

export const clearApplicationStore = () => {
  useApplicationStore.setState({ ...STARTING_STATE });
};

export const searchApplications = ({
  merchantId,
  filter = {},
  nonce = 1,
  setApplications,
  setLoading,
  query
}: {
  merchantId: string;
  setApplications: ApplicationStoreState['setApplications'];
  setLoading: ApplicationStoreState['setLoading'];
  query: typeof useQuery;
  filter?: TApplicationFilter;
  nonce?: number;
}) =>
  query(
    ['application', JSON.stringify(filter), nonce],
    async () => {
      setLoading(true);
      // for some reason this endpoint doesn't unpack left/right
      const response = await Request.safeGet({
        path: `${APIEndPoints.applications}/${merchantId}?access_token`,
        params: Object.fromEntries(
          Object.entries(filter).filter(([, v]) => !!v)
        ),
        schema: z.any()
      });
      if (response.type === 'right') {
        return response.value;
      }
      throw new Error('Failed to search applications', {
        cause: response.value
      });
    },
    {
      onSuccess: (data: Array<TApplication>) => {
        setApplications(data);
        setLoading(false);
      },
      onError: (error) => {
        toast.error(
          'Something went wrong while trying to search applications. Please refresh and try again.'
        );
        setApplications([]);
        setLoading(false);
      },
      // staleTime: 24 * HOUR // Unlikely to change frequently
      // cacheTime: 0,
      refetchInterval: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      retry: 1,
      retryDelay: 2000
    }
  );

export const cancelApplication = ({
  applicationId,
  merchantId,
  setCancelLoading,
  mutate
}: {
  applicationId: TApplication['loanApplicationId'];
  merchantId: string;
  setCancelLoading: ApplicationStoreState['setCancelLoading'];
  mutate: typeof useMutation;
}) =>
  mutate({
    mutationFn: () => {
      setCancelLoading(true);
      return Request.post(
        `${APIEndPoints.applications}/${applicationId}/cancel?access_token`,
        z.any(),
        { applicationId, merchantId }
      );
    },
    onError: (error) => {
      toast.error('Error encountered cancelling application');
    },
    onSettled: () => {
      setCancelLoading(false);
    }
  });
